import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    Validator,
    Validators,
    ReactiveFormsModule,
    FormControl,
    FormBuilder,
    FormGroup
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import {
    CUSTOM_DATE_ADAPTER_CONFIG,
    CustomDateAdapter,
    getDateWithDayOffset
} from 'src/app/shared/utils/date-utils';
import { TransactionDatesModel } from './transaction-dates-model';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { sv } from 'date-fns/locale';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import {
    defaultLocale,
    supportedLocales
} from '../../../features/settings/state/settings.store';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';

@Component({
    selector: 'sb-transaction-dates',
    templateUrl: './transaction-dates.component.html',
    styleUrls: ['./transaction-dates.component.scss'],
    providers: [
        {
            provide: CUSTOM_DATE_ADAPTER_CONFIG,
            useValue: {
                useUTC: true
            }
        },
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: sv
        },
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: TransactionDatesComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: TransactionDatesComponent
        }
    ],
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatDateFnsModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        MatInputModule,
        TablerIconsModule
    ],
    standalone: true
})
export class TransactionDatesComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    get offsetSettlementDateDays(): number {
        return this._offsetSettlementDateDays;
    }

    @Input() set offsetSettlementDateDays(value: number) {
        this._offsetSettlementDateDays = value;
        if (this.dateForm) {
            this.dateForm.controls.settlementDate.patchValue(
                this.getOffsetSettlementDate(this.dateForm.value.transactionDate)
            );
        }
    }
    unsubscribe$ = new Subject();
    /**
     * Determines whether or not to display time part of transaction date.
     * !!! NOT IMPLEMENTED YET !!! - Will have to use an extension of date picker component
     */
    @Input()
    displayTradeTime: boolean = true;

    /**
     * Will copy the value selected in trade date to settlement date.
     */
    @Input()
    copyTransactionDate: boolean = true;

    /**
     * Will initialize transaction date picker with today's date, and
     * initialize settlement date picker with today's date plus any offset configuration.
     */
    @Input()
    initWithCurrentDate: boolean = true;

    /**
     * If copying transaction date this will offset that date
     * in the settlement date selector with a specified number of days.
     */
    private _offsetSettlementDateDays: number = 0;

    /**
     * Default is to skip weekends when offsetting settlement date.
     * Set to true if you do not want them skipped and settlement date
     * can be a Saturday or Sunday.
     */
    @Input()
    offsetIncludeWeekends: boolean = false;

    @Input()
    transactionDateLabel: string = 'Transaction Date';

    @Input()
    transactionDateHint: string = 'Date of transaction';

    @Input()
    settlementDateLabel: string = 'Settlement Date';

    @Input()
    settlementDateHint: string = 'Date when transaction and payment settled';

    @Input() isTransactionDateVisible: boolean = true;

    get initialDate() {
        return this.initWithCurrentDate ? this.getDateWithoutTimestamp(new Date()) : null;
    }

    dateForm: FormGroup;

    onTouched: Function = () => {};
    onChange: Function = (dates) => {};
    onChangeSubs: Subscription[] = [];

    public transactionDate = new FormControl();
    public settlementDate = new FormControl();

    constructor(
        private _fb: FormBuilder,
        private _adapter: DateAdapter<any>,
        private _userSettingsQuery: UserSettingsQuery
    ) {
        this._adapter.setLocale(sv);
        this._userSettingsQuery
            .selectGlobalSetting$('LOCALE')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((culture) => {
                let locale = culture.substring(0, culture.indexOf('-')) || culture;
                // check support for locale...
                if (supportedLocales.indexOf(locale) > -1) {
                    if (locale === 'en') {
                        locale = 'en-US';
                    }
                    // ...and load it if supported
                    return import(
                        /* webpackInclude: /(no|nb|sv|en-GB|en-US|fi|da)\/index\.js$/ */
                        `date-fns/locale/${locale}`
                    ).then((module) => this._adapter.setLocale(module.default));
                } else {
                    // ...or load default locale
                    return import(
                        /* webpackInclude: /(no|nb|sv|en-GB|en-US|fi|da)\/index\.js$/ */
                        `date-fns/locale/${defaultLocale}`
                    ).then((module) => this._adapter.setLocale(module.default));
                }
            });
    }

    ngOnInit() {
        this.dateForm = this._fb.group({
            transactionDate: [this.initialDate, [Validators.required]],
            settlementDate: [
                this.getOffsetSettlementDate(this.initialDate),
                [Validators.required]
            ]
        });

        this.dateForm.controls.transactionDate.valueChanges
            .pipe(
                tap((v) => this.copyToSettlementDate(v)),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    writeValue(dates: TransactionDatesModel): void {
        // If dates are passed in externally --> use those.
        if (dates) {
            this.dateForm.setValue(dates, { emitEvent: false });
        } else if (this.initWithCurrentDate) {
            // Notify parent form about values if set during form initialization.
            // Has to be done async since value changes are not registered during writeValue stage of ControlValueAccessor.
            setTimeout(() => {
                // Note that calling onChange will mark the AbstractControl as dirty for any FormGroup using this Control.
                this.onChange(this.dateForm.value);
            });
        }
    }

    registerOnChange(onChange: any): void {
        const sub = this.dateForm.valueChanges.subscribe(onChange);
        this.onChangeSubs.push(sub);
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any): void {
        this.onTouched = onTouched;
    }

    setDisabledState?(disabled: boolean): void {
        if (disabled) {
            this.dateForm.disable();
        } else {
            this.dateForm.enable();
        }
    }

    getDateWithoutTimestamp(date: Date) {
        return new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
        );
    }

    copyToSettlementDate(transactionDate: Date) {
        if (!this.copyTransactionDate) {
            return; // Should not do this unless flag is true
        }

        this.dateForm.controls.settlementDate.setValue(
            this.getOffsetSettlementDate(transactionDate)
        );
    }

    /**
     * Creates a new Date offset with the offsetSettlementDateDays.
     * @returns A settlement date offset with offsetSettlementDateDays if set.
     *          Else a cloned transaction date. Returns null if transactionDate is falsy
     */
    getOffsetSettlementDate(transactionDate: Date): Date {
        if (!transactionDate) {
            return null;
        }
        // TODO: replace with date-fns
        return this.offsetSettlementDateDays
            ? getDateWithDayOffset(
                  new Date(transactionDate.valueOf()),
                  this.offsetSettlementDateDays,
                  this.offsetIncludeWeekends
              )
            : new Date(transactionDate.valueOf());
    }

    validate(control: AbstractControl) {
        if (this.dateForm.valid) {
            return null;
        }

        let errors: any = {};

        errors = this.addControlErrors(errors, 'transactionDate');
        errors = this.addControlErrors(errors, 'settlementDate');

        return errors;
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = { ...allErrors };
        const controlErrors = this.dateForm.controls[controlName].errors;

        if (controlErrors) {
            errors[controlName] = controlErrors;
        }

        return errors;
    }
}
